import { accessApi, type UserListFilters } from "@services/accessApi";
import { useApiResource } from "@hooks/monitoring/useApiResource";
import type { AuditFilters } from "@models/access";

/** Cache keys of the access pages; a mutation reloads the ones it changed. */
export const ACCESS_KEYS = {
  scope: "access:scope",
  tree: "access:tree",
  roles: "access:roles",
  permissions: "access:permissions",
  openPlans: "access:plans:open",
  users: (f: UserListFilters) =>
    `access:users:${f.organisationId}:${f.includeDescendants ? "tree" : "one"}:${f.q ?? ""}:${f.status ?? ""}:${f.offset ?? 0}`,
  user: (userId: string) => `access:user:${userId}`,
  effective: (userId: string, organisationId: string) => `access:effective:${userId}:${organisationId}`,
  plan: (planId: string) => `access:plan:${planId}`,
  licences: "access:licences",
  instances: (organisationId: string) => `access:instances:${organisationId}`,
  audit: (f: AuditFilters) => `access:audit:${f.from ?? ""}:${f.to ?? ""}:${f.actorId ?? ""}:${f.action ?? ""}:${f.cursor ?? ""}`,
} as const;

export const useAccessScope = () => useApiResource(ACCESS_KEYS.scope, (signal) => accessApi().scope(signal));

export const useOrganisationTree = () =>
  useApiResource(ACCESS_KEYS.tree, (signal) => accessApi().organisationTree(signal));

export const useRoles = () => useApiResource(ACCESS_KEYS.roles, (signal) => accessApi().roles(signal));

export const usePermissionCatalogue = () =>
  useApiResource(ACCESS_KEYS.permissions, (signal) => accessApi().permissions(signal));

/** Open catalogue plans (at most one); listing them needs admin.access.permissions.manage. */
export const useOpenPlans = (enabled: boolean) =>
  useApiResource(enabled ? ACCESS_KEYS.openPlans : null, (signal) => accessApi().plans("open", signal));

/** `null` filters (no organisation chosen yet) load nothing. */
export const useAccessUsers = (filters: UserListFilters | null) =>
  useApiResource(filters ? ACCESS_KEYS.users(filters) : null, (signal) => accessApi().users(filters!, signal));

export const useAccessUser = (userId: string | null) =>
  useApiResource(userId ? ACCESS_KEYS.user(userId) : null, (signal) => accessApi().user(userId!, signal));

export const useEffectivePermissions = (userId: string | null, organisationId: string | null) =>
  useApiResource(
    userId && organisationId ? ACCESS_KEYS.effective(userId, organisationId) : null,
    (signal) => accessApi().effectivePermissions(userId!, organisationId!, signal),
  );

export const useAudit = (filters: AuditFilters) =>
  useApiResource(ACCESS_KEYS.audit(filters), (signal) => accessApi().audit(filters, signal));

export const useLicences = (enabled = true) =>
  useApiResource(enabled ? ACCESS_KEYS.licences : null, (signal) => accessApi().licences(signal));

/** The organisation's licence instances of this application, each with its API keys. */
export const useLicenceInstances = (organisationId: string | null) =>
  useApiResource(organisationId ? ACCESS_KEYS.instances(organisationId) : null,
                 (signal) => accessApi().instances(organisationId!, signal));
